var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var JoinV2Config_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import './advancedJoinColumns'; // force import json schema
import { Operator } from './advancedJoinColumns';
import { JoinType } from './enums';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult, ToolValidationState } from './validation/toolValidationResult';
import { getRenameColumnsV2 } from './toolConfigUtil';
import { joinV2ConfigSchemaValidation } from '../validations.generated';
export const joinV2ConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:JoinV2Config',
    type: 'object',
    properties: {
        joinType: {
            type: 'string',
            enum: Object.values(JoinType)
        },
        joins: {
            type: 'array',
            items: { $ref: 'urn:wisepipe-common-pipe:AdvancedJoinColumns' },
            minItems: 1
        },
        caseInsensitive: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:BoolToolVariable'
                },
                { type: 'boolean' }
            ]
        }
    },
    required: ['joinType', 'joins']
};
let JoinV2Config = JoinV2Config_1 = class JoinV2Config extends ToolConfig {
    constructor(joinType, joins, customFormats, caseInsensitive) {
        super(ToolType.JOINV2, customFormats);
        this.joinType = joinType;
        this.joins = joins;
        this.caseInsensitive = caseInsensitive;
        JSON.validate(this, JoinV2Config_1);
    }
    get left() {
        return JoinV2Config_1.left;
    }
    get right() {
        return JoinV2Config_1.right;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return JoinV2Config_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return JoinV2Config_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return JoinV2Config_1.outs;
    }
    static get left() {
        return JoinV2Config_1.ins[0];
    }
    static get right() {
        return JoinV2Config_1.ins[1];
    }
    static get out() {
        return JoinV2Config_1.outs[0];
    }
    static get ins() {
        return ['left', 'right'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.JOIN.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.COMBINE], this.ins, this.outs, ['merge', 'combine', 'match', 'append columns'], true);
    }
    applyParameters(parameters) {
        const baseVals = this.getBaseValues(parameters);
        return new JoinV2Config_1(baseVals.joinType, parameters.getAppliedVals(this.joins), this.customFormats, baseVals.caseInsensitive);
    }
    getBaseValues(parameters) {
        return {
            joinType: this.joinType,
            joins: this.joins.map((j) => j.getBaseValues(parameters)),
            caseInsensitive: parameters.getBoolOrUndefVal(this.caseInsensitive)
        };
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const exp = tsb.export(this.out);
        const baseVals = this.getBaseValues(iConfigHelper.getParameters());
        const left = ins.schemas.at(this.left);
        const right = ins.schemas.at(this.right);
        const leftJoinCols = new Set();
        const rightJoinCols = new Set();
        const colsToRetainFromRightJoin = new Set();
        baseVals.joins.forEach((join) => {
            leftJoinCols.add(join.leftColumn);
            rightJoinCols.add(join.rightColumn);
            if (join.leftColumn === join.rightColumn && join.operator !== undefined && join.operator !== Operator.EQUAL) {
                // we do not want to remove the rhs columns of these joins as they are not duplicates
                // note that an undefined operator is treated as Operator.EQUAL
                colsToRetainFromRightJoin.add(join.rightColumn);
            }
        });
        // generate the dependents, dependents would be the join columns on the left/right sides
        const dependents = [];
        left.forEach((colMet) => {
            if (leftJoinCols.has(colMet.name))
                dependents.push(colMet);
        });
        right.forEach((colMet) => {
            if (rightJoinCols.has(colMet.name))
                dependents.push(colMet);
        });
        if (baseVals.joinType === JoinType.ANTI) {
            left.forEach((c) => {
                // need to add rhs join cols as dependee to each join column
                if (leftJoinCols.has(c.name))
                    exp.copy(this.left, c, false, true, dependents);
                else
                    exp.copy(this.left, c, false, false);
            });
        }
        else {
            // for equality joins only:
            // remove rhs column if joining with a column on the left with the same name - it's a total duplicate
            // better to remove it than end up with a useless aliased <column>-2.
            const dupes = baseVals.joins.filter((j) => j.leftColumn === j.rightColumn).map((j) => j.rightColumn);
            const deduplicated = right.filter((r) => colsToRetainFromRightJoin.has(r.name) || !dupes.includes(r.name));
            const leftColumns = left.map((l) => l.name);
            const rightColumns = deduplicated.map((r) => r.name);
            const renames = getRenameColumnsV2(leftColumns, rightColumns);
            left.forEach((c) => {
                if (leftJoinCols.has(c.name))
                    exp.copy(this.left, c, false, true, dependents);
                else
                    exp.copy(this.left, c, false, false);
            });
            deduplicated.forEach((c) => {
                if (rightJoinCols.has(c.name))
                    exp.copy(this.right, c, false, true, dependents, renames.get(c.name));
                else
                    exp.copy(this.right, c, false, false, undefined, renames.get(c.name));
            });
            exp.initializeColumnFormats().setColumnFormats(this.customFormats);
        }
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const left = scope.schemas.at(this.left);
        const right = scope.schemas.at(this.right);
        if (!left || !right) {
            return ToolValidationResult.getPortMissingEdgesResult('One or more input nodes are missing.', 'The join tool requires two input nodes.');
        }
        const baseVals = this.getBaseValues(iConfigHelper.getParameters());
        for (const joinCol of baseVals.joins) {
            const findLeft = left.find((col) => col.name === joinCol.leftColumn);
            const findRight = right.find((col) => col.name === joinCol.rightColumn);
            // check that both columns exist
            if (!findLeft) {
                return ToolValidationResult.getBadMetadataResult(`The left column '${joinCol.leftColumn}' is missing from the left input node.`, `Please select a column from the left input node. Available columns: ${left
                    .map((col) => col.name)
                    .join(', ')}.`);
            }
            if (!findRight) {
                return ToolValidationResult.getBadMetadataResult(`The right column '${joinCol.rightColumn}' is missing from the right input node.`, `Please select a column from the right input node. Available columns: ${right
                    .map((col) => col.name)
                    .join(', ')}.`);
            }
            if (!ToolConfig.columnTypesAreEqual(findLeft.type, findRight.type)) {
                return ToolValidationResult.getBadMetadataResult(`The left and right columns are different types. Left column type: ${findLeft.type}. Right column type: ${findRight.type}.`, 'Select columns of the same type or convert one of the column type.');
            }
            // if both columns are period columns, check that they have the same calendarID and level
            if (findLeft.type === 'period' && findRight.type === 'period') {
                const leftColumnMetadata = findLeft.column.metadata;
                const rightColumnMetadata = findRight.column.metadata;
                if (leftColumnMetadata?.calendarID !== rightColumnMetadata?.calendarID ||
                    leftColumnMetadata?.level !== rightColumnMetadata?.level) {
                    return ToolValidationResult.getBadMetadataResult('The left and right columns have different calendar IDs or levels.');
                }
            }
        }
        return ToolValidationResult.getValidResult();
    }
    isValidForRun(iConfigHelper, scope, pass) {
        return pass ? ToolValidationState.BAD_METADATA : this.isValidForCalc(iConfigHelper, scope);
    }
};
JoinV2Config = JoinV2Config_1 = __decorate([
    JsonSchemaV2(joinV2ConfigSchema, joinV2ConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.JOINV2)
], JoinV2Config);
export { JoinV2Config };
//# sourceMappingURL=joinV2Config.js.map